package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.runtime.Composable
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.core.util.PdfLine
import br.com.vvrefeicoes.portal.core.util.PdfReport
import br.com.vvrefeicoes.portal.core.util.PercentFormatter
import br.com.vvrefeicoes.portal.domain.model.EntryRow
import br.com.vvrefeicoes.portal.domain.model.EntryRowLabel
import br.com.vvrefeicoes.portal.domain.model.EntryTableKind
import br.com.vvrefeicoes.portal.domain.model.OperationalEntry
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryEvent
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryEventType
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryKind
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryStatus
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_approved_info
import portalvv.shared.generated.resources.operational_company_label
import portalvv.shared.generated.resources.operational_corporate_admin_others_percent
import portalvv.shared.generated.resources.operational_corporate_charges
import portalvv.shared.generated.resources.operational_corporate_payroll
import portalvv.shared.generated.resources.operational_corporate_taxes
import portalvv.shared.generated.resources.operational_corporate_title
import portalvv.shared.generated.resources.operational_history_empty
import portalvv.shared.generated.resources.operational_history_rejected
import portalvv.shared.generated.resources.operational_history_saved
import portalvv.shared.generated.resources.operational_history_title
import portalvv.shared.generated.resources.operational_pdf_title
import portalvv.shared.generated.resources.operational_period_label
import portalvv.shared.generated.resources.operational_responsible_director
import portalvv.shared.generated.resources.operational_responsible_manager
import portalvv.shared.generated.resources.operational_staff_movement_title
import portalvv.shared.generated.resources.operational_status_approved
import portalvv.shared.generated.resources.operational_status_draft
import portalvv.shared.generated.resources.operational_status_label
import portalvv.shared.generated.resources.operational_status_rejected
import portalvv.shared.generated.resources.operational_status_submitted
import portalvv.shared.generated.resources.operational_submitted_info
import portalvv.shared.generated.resources.operational_subtotal
import portalvv.shared.generated.resources.operational_summary_expense
import portalvv.shared.generated.resources.operational_summary_result
import portalvv.shared.generated.resources.operational_summary_revenue
import portalvv.shared.generated.resources.operational_type_label
import portalvv.shared.generated.resources.operational_type_planned
import portalvv.shared.generated.resources.operational_type_real

@Composable
fun buildOperationalEntryReport(
    entry: OperationalEntry,
    directorNames: List<String>,
    managerNames: List<String>,
): PdfReport {
    val lines = mutableListOf<PdfLine>()

    lines += PdfLine.Title(stringResource(Res.string.operational_pdf_title))
    lines += PdfLine.Row(stringResource(Res.string.operational_period_label), MonthCalendar.label(entry.period))
    lines += PdfLine.Row(stringResource(Res.string.operational_company_label), entry.companyName)
    lines += PdfLine.Row(stringResource(Res.string.operational_type_label), typeLabel(entry.kind))
    if (entry.kind == OperationalEntryKind.GESTAO_FINANCEIRA) {
        lines += PdfLine.Row(stringResource(Res.string.operational_status_label), statusLabel(entry.status))
    }
    lines += PdfLine.Row(stringResource(Res.string.operational_responsible_director), directorNames.joinToString(", ").ifEmpty { "-" })
    lines += PdfLine.Row(stringResource(Res.string.operational_responsible_manager), managerNames.joinToString(", ").ifEmpty { "-" })
    lines += PdfLine.Gap

    lines += PdfLine.Heading(stringResource(Res.string.operational_summary_revenue))
    lines += PdfLine.Row(stringResource(Res.string.operational_summary_revenue), money(entry.revenueTotalCents))
    lines += PdfLine.Row(stringResource(Res.string.operational_summary_expense), money(entry.expenseTotalCents))
    lines +=
        PdfLine.Row(
            stringResource(Res.string.operational_summary_result),
            moneyWithPercent(entry.resultCents, entry.resultPercent),
            bold = true,
        )
    lines += PdfLine.Divider

    lines += PdfLine.Heading(stringResource(Res.string.operational_summary_expense))
    entry.tables.forEach { table ->
        if (table.kind == EntryTableKind.REVENUE) {
            lines += PdfLine.Text(stringResource(table.title))
            table.rows.forEach { row ->
                lines += PdfLine.Row(entryRowLabel(row), money(row.totalCents))
            }
            lines += PdfLine.Row(stringResource(Res.string.operational_subtotal), money(table.totalCents), bold = true)
        }
    }
    entry.tables.forEach { table ->
        if (table.kind == EntryTableKind.EXPENSE) {
            lines += PdfLine.Row(stringResource(table.title), money(table.totalCents))
        }
    }
    lines += PdfLine.Row(stringResource(Res.string.operational_corporate_title), money(entry.corporateTotalCents))
    lines += PdfLine.Row(stringResource(Res.string.operational_corporate_payroll), money(entry.corporatePayrollCents))
    lines += PdfLine.Row(stringResource(Res.string.operational_corporate_charges), money(entry.corporateChargesCents))
    lines +=
        PdfLine.Row(
            stringResource(
                Res.string.operational_corporate_admin_others_percent,
                PercentFormatter.format(entry.adminOthersPercent),
            ),
            money(entry.corporateAdminOthersCents),
        )
    lines += PdfLine.Row(stringResource(Res.string.operational_corporate_taxes), money(entry.corporateTaxesCents))
    lines += PdfLine.Row(stringResource(Res.string.operational_subtotal), money(entry.expenseTotalCents), bold = true)
    lines += PdfLine.Divider

    if (entry.staffMovements.isNotEmpty()) {
        lines += PdfLine.Heading(stringResource(Res.string.operational_staff_movement_title))
        entry.staffMovements.forEach { item ->
            lines += PdfLine.Row("${stringResource(item.movement.label())} · ${item.role}", item.quantity.toString())
        }
        lines += PdfLine.Divider
    }

    lines += PdfLine.Heading(stringResource(Res.string.operational_history_title))
    if (entry.history.isEmpty()) {
        lines += PdfLine.Text(stringResource(Res.string.operational_history_empty))
    } else {
        entry.history.asReversed().forEach { event ->
            lines += PdfLine.Text(historyText(event))
        }
    }

    return PdfReport(lines)
}

private fun money(cents: Long): String = "R$ ${MoneyFormatter.formatCents(cents)}"

private fun moneyWithPercent(
    cents: Long,
    percent: Double,
): String = "${money(cents)} (${PercentFormatter.format(percent)}%)"

@Composable
private fun entryRowLabel(row: EntryRow): String =
    when (val label = row.label) {
        is EntryRowLabel.Resource -> stringResource(label.value)
        is EntryRowLabel.Supplier -> label.value
    }

@Composable
private fun typeLabel(kind: OperationalEntryKind): String =
    when (kind) {
        OperationalEntryKind.GESTAO_FINANCEIRA -> stringResource(Res.string.operational_type_real)
        OperationalEntryKind.PROVISIONADOS_MES -> stringResource(Res.string.operational_type_planned)
    }

@Composable
private fun statusLabel(status: OperationalEntryStatus): String =
    when (status) {
        OperationalEntryStatus.DRAFT -> stringResource(Res.string.operational_status_draft)
        OperationalEntryStatus.SUBMITTED -> stringResource(Res.string.operational_status_submitted)
        OperationalEntryStatus.APPROVED -> stringResource(Res.string.operational_status_approved)
        OperationalEntryStatus.REJECTED -> stringResource(Res.string.operational_status_rejected)
    }

@Composable
private fun historyText(event: OperationalEntryEvent): String =
    when (event.type) {
        OperationalEntryEventType.SAVED -> stringResource(Res.string.operational_history_saved, event.actorName, event.at)
        OperationalEntryEventType.SUBMITTED -> stringResource(Res.string.operational_submitted_info, event.actorName, event.at)
        OperationalEntryEventType.APPROVED -> stringResource(Res.string.operational_approved_info, event.actorName, event.at)
        OperationalEntryEventType.REJECTED -> stringResource(Res.string.operational_history_rejected, event.actorName, event.at, event.note)
    }
